import React, { useState, useRef, useEffect } from 'react';
import PressableScale from './motion/PressableScale';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  StyleSheet, 
  Platform, 
  Animated, 
  Easing,
  ScrollView,
  Modal,
  PanResponder
, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import { useBackgroundQuery, QueryStatus } from '../context/BackgroundQueryContext';
import { useNavigation } from '../context/NavigationContext';
import { describeResultCounts } from '../utils/resultCounts';
import { buildBackgroundHandoff, stashBackgroundHandoff, clearLegacyHandoffKeys } from '../utils/backgroundHandoff';
import { isMobile, SCREEN_WIDTH } from '../utils/responsive';
import { AnimatedProgressBar, ElapsedTimeDisplay, GradientViewButton } from './backgroundQueryModalParts';
import { styles } from './backgroundQueryModalStyles';


// Animated Progress Bar with shimmer effect
const BackgroundQueryModal = () => {
  const {
    activeQueries,
    isMinimized,
    setIsMinimized,
    cancelQuery,
    retryQuery,
    removeQuery,
    markQueryViewed,
    getUnviewedCount,
    clearCompletedQueries,
    QueryStatus,
    modalPosition,
    toggleModalPosition,
    IS_MOBILE
  } = useBackgroundQuery();
  
  const { navigateTo } = useNavigation();

  // Animation values
  const slideAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const expandAnim = useRef(new Animated.Value(0)).current;
  const positionAnim = useRef(new Animated.Value(modalPosition === 'bottom-right' ? 0 : 1)).current;
  const spinAnim = useRef(new Animated.Value(0)).current;
  
  // Cancel confirmation modal
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  
  // Mobile bottom sheet state
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  
  // Track previous query count to detect first-ever query on mobile
  const prevQueryCountRef = useRef(0);
  
  // Completion phase: show progress bar at 100% (no shimmer) briefly when a query completes
  const [completionPhaseIds, setCompletionPhaseIds] = useState([]);
  const completionStartedRef = useRef(new Set());
  const isMountedRef = useRef(true);
  
  // Unviewed count
  const unviewedCount = getUnviewedCount();

  // Gradient text styles per query type (web uses CSS gradient, native falls back to solid color)
  const purpleGradientStyle = Platform.OS === 'web'
    ? { background: 'linear-gradient(90deg, #8b5cf6, #c4b5fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
    : { color: '#a78bfa' };
  const greenGradientStyle = Platform.OS === 'web'
    ? { background: 'linear-gradient(90deg, #059669, #34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
    : { color: '#34d399' };
  const blueGradientStyle = Platform.OS === 'web'
    ? { background: 'linear-gradient(90deg, #1d4ed8, #60a5fa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
    : { color: '#60a5fa' };

  // Animate position change
  useEffect(() => {
    if (activeQueries.length === 0) return;
    Animated.spring(positionAnim, {
      toValue: modalPosition === 'bottom-right' ? 0 : 1,
      tension: 60,
      friction: 10,
      useNativeDriver: false
    }).start();
  }, [modalPosition, activeQueries.length]);

  // Animate slide in on mount
  useEffect(() => {
    if (activeQueries.length === 0) return;
    Animated.spring(slideAnim, {
      toValue: 1,
      tension: 50,
      friction: 8,
      useNativeDriver: true
    }).start();
  }, [activeQueries.length]);

  // Spin animation for processing ring on mobile indicator
  useEffect(() => {
    const isProcessing = activeQueries.some(q => q.status === QueryStatus.PROCESSING);
    if (isProcessing) {
      spinAnim.setValue(0);
      const spin = Animated.loop(
        Animated.timing(spinAnim, {
          toValue: 1,
          duration: 900,
          easing: Easing.linear,
          useNativeDriver: true
        })
      );
      spin.start();
      return () => spin.stop();
    }
  }, [activeQueries.map(q => q.status).join(',')]);

  // Auto-open mobile sheet on first query so user sees it's running
  useEffect(() => {
    if (IS_MOBILE && activeQueries.length === 1 && prevQueryCountRef.current === 0) {
      setShowMobileSheet(true);
    }
    prevQueryCountRef.current = activeQueries.length;
  }, [activeQueries.length]);

  // Track component mount state at the component level (not per-effect)
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // When a query becomes COMPLETED, show 100% bar (no shimmer) briefly then show results
  // ⭐ FIX: Use component-level isMountedRef so timeouts always fire even when
  // activeQueries changes between the timeout being scheduled and firing
  useEffect(() => {
    const COMPLETION_DURATION_MS = 450;
    
    activeQueries.forEach((q) => {
      if (q.status === QueryStatus.COMPLETED && !completionStartedRef.current.has(q.id)) {
        completionStartedRef.current.add(q.id);
        setCompletionPhaseIds((prev) => (prev.includes(q.id) ? prev : [...prev, q.id]));
        
        // Timeout uses component-level ref - won't be invalidated by effect re-runs
        setTimeout(() => {
          if (isMountedRef.current) {
            setCompletionPhaseIds((prev) => prev.filter((id) => id !== q.id));
          }
        }, COMPLETION_DURATION_MS);
      }
    });
    
    // Clean up orphaned completion phases for queries that no longer exist
    setCompletionPhaseIds((prev) => {
      const activeIds = new Set(activeQueries.map(q => q.id));
      return prev.filter(id => activeIds.has(id));
    });
  }, [activeQueries]);

  // Pulse animation for unviewed notifications
  useEffect(() => {
    if (activeQueries.length === 0) return;
    if (unviewedCount > 0 && isMinimized) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.1,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true
          })
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [unviewedCount, isMinimized, activeQueries.length]);

  // Expand/collapse animation
  useEffect(() => {
    if (activeQueries.length === 0) return;
    Animated.spring(expandAnim, {
      toValue: isMinimized ? 0 : 1,
      tension: 60,
      friction: 10,
      useNativeDriver: false
    }).start();
  }, [isMinimized, activeQueries.length]);

  // Don't render if no active queries
  if (activeQueries.length === 0) {
    return null;
  }

  // Toggle minimize state
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    
    // Mark all as viewed when expanding
    if (isMinimized) {
      activeQueries
        .filter(q => q.status === QueryStatus.COMPLETED)
        .forEach(q => markQueryViewed(q.id));
    }
  };

  // Handle view results - with navigation fix for same-route navigation
  const handleViewResults = (query) => {
    markQueryViewed(query.id);
    
    // Hand the finished results to SgpFlow in memory. They used to be JSON'd
    // into sessionStorage, which overflowed on big SGP result sets and made
    // View silently re-run the whole search (see utils/backgroundHandoff.js).
    stashBackgroundHandoff(buildBackgroundHandoff(query));
    if (Platform.OS === 'web') {
      try {
        window.sessionStorage.setItem('sgp:lastQuery', query.prompt);
        clearLegacyHandoffKeys(window.sessionStorage);
      } catch (e) {
        console.warn('[BackgroundQueryModal] sessionStorage unavailable:', e);
      }
    }
    
    // Use a unique timestamp to force navigation even to the same route
    const navigationKey = `bg_${query.id}_${Date.now()}`;
    
    // Check if we're already on the results page
    const currentPath = Platform.OS === 'web' ? window.location.pathname : '';
    const isOnResultsPage = currentPath.includes('/results');
    
    if (isOnResultsPage) {
      // If already on results page, need to force a reload/re-render
      // Update URL params directly to trigger route change detection
      if (Platform.OS === 'web') {
        const newUrl = `/results?q=${encodeURIComponent(query.prompt)}&bg=${navigationKey}`;
        window.history.pushState({}, '', newUrl);
        // ⭐ FIX: Small delay to ensure sessionStorage is written before event fires
        // This prevents race condition where event handler runs before data is ready
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('backgroundQueryNavigation', { 
            detail: { queryId: query.id, prompt: query.prompt } 
          }));
        }, 50);
      }
      // ⭐ FIX: Don't navigate again if already on results page - just use the event
      return;
    }
    
    navigateTo('SgpFlow', { 
      payload: { 
        query: query.prompt, 
        fromBackground: true,
        queryId: query.id,
        navigationKey 
      } 
    });
  };

  // Handle cancel - shows confirmation for in-progress, removes immediately for others
  const handleCancelOrRemove = (queryId) => {
    const query = activeQueries.find(q => q.id === queryId);
    if (query?.status === QueryStatus.PROCESSING || query?.status === QueryStatus.PENDING) {
      // Show confirmation for in-progress queries
      setConfirmCancelId(queryId);
    } else {
      // Remove completed/error/cancelled queries immediately
      removeQuery(queryId);
    }
  };

  // Confirm cancel - clear state FIRST to close modal immediately
  const confirmCancel = () => {
    const idToCancel = confirmCancelId;
    // Clear state immediately to close modal
    setConfirmCancelId(null);
    
    // Then perform the cancellation after a microtask to ensure modal closes
    if (idToCancel) {
      setTimeout(() => {
        cancelQuery(idToCancel);
        removeQuery(idToCancel);
      }, 0);
    }
  };
  
  // Dismiss cancel modal
  const dismissCancelModal = () => {
    setConfirmCancelId(null);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case QueryStatus.COMPLETED:
        return '#4ade80';
      case QueryStatus.ERROR:
        return '#f87171';
      case QueryStatus.CANCELLED:
        return '#94a3b8';
      case QueryStatus.PROCESSING:
        return '#60a5fa';
      default:
        return '#fbbf24';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case QueryStatus.COMPLETED:
        return 'checkmark-circle';
      case QueryStatus.ERROR:
        return 'alert-circle';
      case QueryStatus.CANCELLED:
        return 'close-circle';
      case QueryStatus.PROCESSING:
        return 'sync';
      default:
        return 'time';
    }
  };

  // Calculate animated height
  const animatedHeight = expandAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [52, Math.min(400, 80 + activeQueries.length * 90)]
  });

  // Calculate animated width - minimized width needs to fit status dot + badge + chevron
  const animatedWidth = expandAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [120, isMobile ? SCREEN_WIDTH - 32 : 380]
  });

  // Animated horizontal position
  const animatedRight = positionAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [24, SCREEN_WIDTH - (isMobile ? SCREEN_WIDTH - 32 + 24 : 380 + 24)]
  });
  
  const animatedLeft = positionAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [SCREEN_WIDTH - (isMobile ? SCREEN_WIDTH - 32 + 24 : 380 + 24), 24]
  });

  // Animated styles
  const containerStyle = {
    transform: [
      { 
        translateY: slideAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [200, 0]
        })
      },
      { scale: isMinimized && unviewedCount > 0 ? pulseAnim : 1 }
    ],
    height: animatedHeight,
    width: animatedWidth,
    // Use left position for easier animation
    left: modalPosition === 'bottom-left' ? 24 : undefined,
    right: modalPosition === 'bottom-right' ? 24 : undefined
  };

  // Mobile: show just a floating indicator button
  if (IS_MOBILE && isMinimized && !showMobileSheet) {
    return (
      <>
        <Animated.View 
          style={[
            styles.mobileIndicator,
            {
              transform: [
                { 
                  translateY: slideAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [100, 0]
                  })
                },
                { scale: unviewedCount > 0 ? pulseAnim : 1 }
              ],
              right: modalPosition === 'bottom-right' ? 16 : undefined,
              left: modalPosition === 'bottom-left' ? 16 : undefined
            }
          ]}
        >
          <PressableScale 
            style={styles.mobileIndicatorButton}
            onPress={() => setShowMobileSheet(true)}
          >
            <LinearGradient
              colors={['#8b5cf6', '#6d28d9']}
              style={styles.mobileIndicatorGradient}
            >
              <Ionicons 
                name={activeQueries.some(q => q.status === QueryStatus.PROCESSING) ? 'sync' : 'layers'} 
                size={22} 
                color="#fff" 
              />
              {unviewedCount > 0 && (
                <View style={styles.mobileIndicatorBadge}>
                  <Text style={styles.badgeText}>{unviewedCount}</Text>
                </View>
              )}
              {activeQueries.some(q => q.status === QueryStatus.PROCESSING) && (
                <Animated.View style={[styles.processingRing, {
                  transform: [{
                    rotate: spinAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '360deg']
                    })
                  }]
                }]} />
              )}
            </LinearGradient>
          </PressableScale>
        </Animated.View>

        {/* Mobile Bottom Sheet */}
        <Modal
          visible={showMobileSheet}
          transparent
          animationType="slide"
          onRequestClose={() => setShowMobileSheet(false)}
        >
          <Pressable 
            style={styles.mobileSheetOverlay}
            onPress={() => setShowMobileSheet(false)}
          >
            <View style={styles.mobileSheet}>
              {/* Handle bar */}
              <View style={styles.mobileSheetHandle} />
              
              {/* Header */}
              <View style={styles.mobileSheetHeader}>
                <Text style={styles.mobileSheetTitle}>
                  Background Queries ({activeQueries.length})
                </Text>
                <PressableScale onPress={() => setShowMobileSheet(false)}>
                  <Ionicons name="close" size={24} color="rgba(255,255,255,0.7)" />
                </PressableScale>
              </View>

              {/* Queries list */}
              <ScrollView style={styles.mobileSheetContent}>
                {activeQueries.map((query, index) => {
                  const isSGP = query.sgpCount > 0 || query.isSGPMode;
                  const isLive = !!(query.isLive || query._queryClassification?.isLive || query._queryClassification?.entities?.isLive);
                  const isStraight = !isSGP && !isLive;
                  const completedSGP = isSGP && query.status === QueryStatus.COMPLETED;
                  const borderColor = isLive ? '#10B981' : isSGP ? '#8B5CF6' : '#3B82F6';
                  return (
                  <View key={query.id} style={[styles.queryCard, { borderLeftWidth: 2, borderLeftColor: borderColor }]}>
                    {/* Query header */}
                    <View style={styles.queryHeader}>
                      <View style={styles.queryTitleRow}>
                        <Ionicons 
                          name={getStatusIcon(query.status)} 
                          size={16} 
                          color={isLive ? '#10B981' : completedSGP ? '#8B5CF6' : isStraight ? '#60a5fa' : getStatusColor(query.status)} 
                        />
                        {isLive && (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: 'rgba(16,185,129,0.18)', borderRadius: 3, paddingHorizontal: 5, paddingVertical: 1, borderWidth: 1, borderColor: 'rgba(16,185,129,0.5)', marginRight: 2 }}>
                            <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: '#10B981' }} />
                            <Text style={{ color: '#10B981', fontSize: 9, fontWeight: '800', letterSpacing: 0.4 }}>LIVE</Text>
                          </View>
                        )}
                        {isSGP && !isLive && (
                          <View style={{ backgroundColor: 'rgba(139,92,246,0.2)', borderRadius: 3, paddingHorizontal: 4, paddingVertical: 1, borderWidth: 1, borderColor: 'rgba(139,92,246,0.5)', marginRight: 2 }}>
                            <Text style={{ color: '#a78bfa', fontSize: 9, fontWeight: '800', letterSpacing: 0.4 }}>SGP</Text>
                          </View>
                        )}
                        {isStraight && (
                          <View style={{ backgroundColor: 'rgba(59,130,246,0.15)', borderRadius: 3, paddingHorizontal: 4, paddingVertical: 1, borderWidth: 1, borderColor: 'rgba(59,130,246,0.4)', marginRight: 2 }}>
                            <Text style={{ color: '#93c5fd', fontSize: 9, fontWeight: '700', letterSpacing: 0.4 }}>STRAIGHT</Text>
                          </View>
                        )}
                        <Text style={styles.queryPrompt} numberOfLines={2}>
                          {query.prompt || 'Query'}
                        </Text>
                      </View>
                      
                      <PressableScale 
                        onPress={() => handleCancelOrRemove(query.id)}
                        style={styles.closeButton}
                      >
                        <Ionicons name="close" size={16} color="rgba(255,255,255,0.5)" />
                      </PressableScale>
                    </View>

                    {/* Progress bar - animated (loading or completion phase) */}
                    {(query.status === QueryStatus.PENDING || query.status === QueryStatus.PROCESSING || (query.status === QueryStatus.COMPLETED && completionPhaseIds.includes(query.id))) && (
                      <View style={styles.progressContainer}>
                        <AnimatedProgressBar 
                          progress={query.status === QueryStatus.COMPLETED ? 100 : (query.progress || 0)} 
                          isProcessing={query.status === QueryStatus.PROCESSING}
                        />
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={styles.progressText}>
                              {query.status === QueryStatus.COMPLETED && completionPhaseIds.includes(query.id)
                                ? 'Complete'
                                : (query.progressMessage || 'Querying odds data...')}
                            </Text>
                            {query.startTime && query.status !== QueryStatus.COMPLETED && (
                              <ElapsedTimeDisplay startTime={query.startTime} />
                            )}
                          </View>
                          {query.status !== QueryStatus.COMPLETED && (
                            <PressableScale 
                              onPress={() => setConfirmCancelId(query.id)}
                              style={{ paddingVertical: 4, paddingHorizontal: 8 }}
                            >
                              <Text style={{ color: '#f87171', fontSize: 12, fontWeight: '600' }}>Cancel</Text>
                            </PressableScale>
                          )}
                        </View>
                      </View>
                    )}

                    {/* Results / Actions (after completion phase finishes) */}
                    {query.status === QueryStatus.COMPLETED && !completionPhaseIds.includes(query.id) && (() => {
                      const focusBook = query._preferredSportsbook;
                      const focusCount = focusBook ? (query.allBooksCount ?? 0) : null;
                      const hasZeroForFocusBook = focusBook && focusCount === 0;
                      const hasBelowThreshold = !focusBook && !isSGP
                        && (query.allBooksCount ?? 0) === 0
                        && (query.rawResultCount ?? 0) > 0;
                      const _REASON_LABELS = {
                        browse_query_no_cache: 'Not cached yet',
                        exhausted_retries: 'Data not available',
                        no_api_priced_sgps: 'No SGPs priced',
                      };
                      const hasCacheReason = !focusBook
                        && (query.allBooksCount ?? 0) === 0
                        && (query.rawResultCount ?? 0) === 0
                        && !!query._cacheReason;
                      const typeGradient = isLive ? greenGradientStyle : isStraight ? blueGradientStyle : purpleGradientStyle;
                      return (
                        <View style={styles.resultsRow}>
                          <View style={{ flex: 1, gap: 2 }}>
                            {hasZeroForFocusBook ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <Ionicons name="alert-circle-outline" size={13} color="#f59e0b" />
                                <Text style={{ fontSize: 12, color: '#f59e0b', fontWeight: '600' }}>
                                  {`No results for ${focusBook}`}
                                </Text>
                              </View>
                            ) : hasBelowThreshold ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <Ionicons name="information-circle-outline" size={13} color="rgba(255,255,255,0.4)" />
                                <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: '600' }}>
                                  {query.hiddenText || `${query.rawResultCount} lines hidden by your filters`}
                                </Text>
                              </View>
                            ) : hasCacheReason ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <Ionicons name="information-circle-outline" size={13} color="rgba(255,255,255,0.35)" />
                                <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: '600' }}>
                                  {_REASON_LABELS[query._cacheReason] || 'No results'}
                                </Text>
                              </View>
                            ) : !isSGP && query.allBooksCount != null ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                <Text style={[styles.resultsText, typeGradient]}>
                                  {describeResultCounts({ lines: query.allBooksCount, cards: query.allBooksCards })}
                                </Text>
                                {query.hasMyBooks && <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>·</Text>}
                                {query.hasMyBooks && <Text style={[{ fontSize: 12, fontWeight: '600' }, typeGradient]}>
                                  {`${query.myBooksCount} on My Books`}
                                </Text>}
                                {!!query.unreliableOnlyBooks && <Text style={{ fontSize: 11, color: '#f59e0b' }}>• prediction markets only</Text>}
                                {query.cached && <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>• Cached</Text>}
                                {!!query.durationMs && <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>• {(query.durationMs / 1000).toFixed(1)}s</Text>}
                              </View>
                            ) : (
                              <Text style={[styles.resultsText, isSGP && { color: '#a78bfa' }, isLive && { color: '#6ee7b7' }, isStraight && { color: '#93c5fd' }]}>
                                {query.sgpCount > 0
                                  ? `${query.sgpCount} SGPs`
                                  : isSGP ? '0 SGPs'
                                  : focusCount != null ? `${focusCount} lines`
                                  : `${query.results?.length || 0} lines`}
                                {query.cached && ' • Cached'}
                                {query.durationMs && ` • ${(query.durationMs / 1000).toFixed(1)}s`}
                              </Text>
                            )}
                          </View>
                          {!hasZeroForFocusBook && (
                            <GradientViewButton
                              isSGP={isSGP}
                              isLive={isLive}
                              onPress={() => {
                                setShowMobileSheet(false);
                                handleViewResults(query);
                              }}
                            />
                          )}
                        </View>
                      );
                    })()}

                    {/* Error state */}
                    {query.status === QueryStatus.ERROR && (
                      <View style={styles.errorRow}>
                        <Ionicons name="warning" size={14} color="#f87171" />
                        <Text style={styles.errorText} numberOfLines={1}>
                          {query.error || 'Query failed'}
                        </Text>
                        <PressableScale
                          style={styles.rerunButton}
                          onPress={() => retryQuery(query.id)}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <Ionicons name="refresh" size={12} color="#fca5a5" />
                          <Text style={styles.rerunButtonText}>Rerun</Text>
                        </PressableScale>
                      </View>
                    )}
                  </View>
                  );
                })}
              </ScrollView>
            </View>
          </Pressable>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Animated.View style={[styles.container, containerStyle]}>
        {/* Glassmorphism background */}
        <BlurView 
          intensity={80} 
          tint="dark" 
          style={styles.blurBackground}
        >
          <LinearGradient
            colors={['rgba(30, 30, 40, 0.95)', 'rgba(20, 20, 30, 0.98)']}
            style={styles.gradientOverlay}
          />
        </BlurView>

        {/* Header / Minimized state */}
        <PressableScale 
          style={[styles.header, isMinimized && styles.headerMinimized]} 
          onPress={toggleMinimize}
        >
          <View style={[styles.headerLeft, isMinimized && styles.headerLeftMinimized]}>
            {isMinimized ? (
              <>
                <View style={styles.statusDotContainer}>
                  <View style={[styles.statusDotSmall, { backgroundColor: getStatusColor(activeQueries[0]?.status) }]} />
                  {unviewedCount > 0 && (
                    <View style={styles.badgeSmall}>
                      <Text style={styles.badgeTextSmall}>{unviewedCount}</Text>
                    </View>
                  )}
                </View>
                <Text style={styles.minimizedLabel}>{activeQueries.length}</Text>
              </>
            ) : (
              <>
                <Ionicons name="layers" size={18} color="#c4b5fd" />
                <Text style={styles.headerTitle}>
                  Background Queries ({activeQueries.length})
                </Text>
              </>
            )}
          </View>
          
          <View style={styles.headerRight}>
            {/* Position toggle button - only show when expanded on desktop */}
            {!isMinimized && !isMobile && (
              <PressableScale 
                onPress={toggleModalPosition} 
                style={styles.positionButton}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons 
                  name={modalPosition === 'bottom-right' ? 'chevron-back' : 'chevron-forward'} 
                  size={14} 
                  color="rgba(255,255,255,0.6)" 
                />
              </PressableScale>
            )}
            
            <PressableScale onPress={toggleMinimize} style={styles.chevronButton}>
              <Ionicons 
                name={isMinimized ? 'chevron-up' : 'chevron-down'} 
                size={16} 
                color="rgba(255,255,255,0.5)" 
              />
            </PressableScale>
          </View>
        </PressableScale>

        {/* Expanded content */}
        {!isMinimized && (
          <ScrollView 
            style={styles.queriesList}
            showsVerticalScrollIndicator={false}
          >
            {activeQueries.map((query, index) => {
              const isSGP = query.sgpCount > 0 || query.isSGPMode;
              const isLive = !!(query.isLive || query._queryClassification?.isLive || query._queryClassification?.entities?.isLive);
              const isStraight = !isSGP && !isLive;
              const completedSGP = isSGP && query.status === QueryStatus.COMPLETED;
              const borderColor = isLive ? '#10B981' : isSGP ? '#8B5CF6' : '#3B82F6';
              return (
              <View 
                key={query.id} 
                style={[
                  styles.queryCard,
                  index === activeQueries.length - 1 && { marginBottom: 8 },
                  { borderLeftWidth: 2, borderLeftColor: borderColor }
                ]}
              >
                {/* Query header */}
                <View style={styles.queryHeader}>
                  <View style={styles.queryTitleRow}>
                    <Ionicons 
                      name={getStatusIcon(query.status)} 
                      size={16} 
                      color={isLive ? '#10B981' : completedSGP ? '#8B5CF6' : isStraight ? '#60a5fa' : getStatusColor(query.status)} 
                    />
                    {isLive && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: 'rgba(16,185,129,0.18)', borderRadius: 3, paddingHorizontal: 5, paddingVertical: 1, borderWidth: 1, borderColor: 'rgba(16,185,129,0.5)', marginRight: 2 }}>
                        <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: '#10B981' }} />
                        <Text style={{ color: '#10B981', fontSize: 9, fontWeight: '800', letterSpacing: 0.4 }}>LIVE</Text>
                      </View>
                    )}
                    {isSGP && !isLive && (
                      <View style={{ backgroundColor: 'rgba(139,92,246,0.2)', borderRadius: 3, paddingHorizontal: 4, paddingVertical: 1, borderWidth: 1, borderColor: 'rgba(139,92,246,0.5)', marginRight: 2 }}>
                        <Text style={{ color: '#a78bfa', fontSize: 9, fontWeight: '800', letterSpacing: 0.4 }}>SGP</Text>
                      </View>
                    )}
                    {isStraight && (
                      <View style={{ backgroundColor: 'rgba(59,130,246,0.15)', borderRadius: 3, paddingHorizontal: 4, paddingVertical: 1, borderWidth: 1, borderColor: 'rgba(59,130,246,0.4)', marginRight: 2 }}>
                        <Text style={{ color: '#93c5fd', fontSize: 9, fontWeight: '700', letterSpacing: 0.4 }}>STRAIGHT</Text>
                      </View>
                    )}
                    <Text style={styles.queryPrompt} numberOfLines={1}>
                      {query.prompt || 'Query'}
                    </Text>
                  </View>
                  
                  <PressableScale 
                    onPress={() => handleCancelOrRemove(query.id)}
                    style={styles.closeButton}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons name="close" size={16} color="rgba(255,255,255,0.5)" />
                  </PressableScale>
                </View>

                {/* Progress bar - animated (loading or completion phase) */}
                {(query.status === QueryStatus.PENDING || query.status === QueryStatus.PROCESSING || (query.status === QueryStatus.COMPLETED && completionPhaseIds.includes(query.id))) && (
                  <View style={styles.progressContainer}>
                    <AnimatedProgressBar 
                      progress={query.status === QueryStatus.COMPLETED ? 100 : (query.progress || 0)} 
                      isProcessing={query.status === QueryStatus.PROCESSING}
                    />
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <Text style={styles.progressText}>
                          {query.status === QueryStatus.COMPLETED && completionPhaseIds.includes(query.id)
                            ? 'Complete'
                            : (query.progressMessage || 'Querying odds data...')}
                        </Text>
                        {query.startTime && query.status !== QueryStatus.COMPLETED && (
                          <ElapsedTimeDisplay startTime={query.startTime} />
                        )}
                      </View>
                      {query.status !== QueryStatus.COMPLETED && (
                        <PressableScale 
                          onPress={() => setConfirmCancelId(query.id)}
                          style={{ paddingVertical: 2, paddingHorizontal: 6 }}
                        >
                          <Text style={{ color: '#f87171', fontSize: 11, fontWeight: '600' }}>Cancel</Text>
                        </PressableScale>
                      )}
                    </View>
                  </View>
                )}

                {/* Results / Actions (after completion phase finishes) */}
                {query.status === QueryStatus.COMPLETED && !completionPhaseIds.includes(query.id) && (() => {
                  const focusBook = query._preferredSportsbook;
                  const focusCount = focusBook ? (query.allBooksCount ?? 0) : null;
                  const hasZeroForFocusBook = focusBook && focusCount === 0;
                  // Results exist but ALL fail the min-books threshold (no focus book involved)
                  const hasBelowThreshold = !focusBook && !isSGP
                    && (query.allBooksCount ?? 0) === 0
                    && (query.rawResultCount ?? 0) > 0;
                  const _REASON_LABELS = {
                    browse_query_no_cache: 'Not cached yet',
                    exhausted_retries: 'Data not available',
                    no_api_priced_sgps: 'No SGPs priced',
                  };
                  const hasCacheReason = !focusBook
                    && (query.allBooksCount ?? 0) === 0
                    && (query.rawResultCount ?? 0) === 0
                    && !!query._cacheReason;
                  const typeGradient = isLive ? greenGradientStyle : isStraight ? blueGradientStyle : purpleGradientStyle;
                  return (
                    <View style={styles.resultsRow}>
                      <View style={{ flex: 1, gap: 2 }}>
                        {hasZeroForFocusBook ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Ionicons name="alert-circle-outline" size={13} color="#f59e0b" />
                            <Text style={{ fontSize: 12, color: '#f59e0b', fontWeight: '600' }}>
                              {`No results for ${focusBook}`}
                            </Text>
                          </View>
                        ) : hasBelowThreshold ? (
                          <View style={{ gap: 2 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                              <Ionicons name="information-circle-outline" size={13} color="rgba(255,255,255,0.4)" />
                              <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: '600' }}>
                                {query.hiddenText || `${query.rawResultCount} lines hidden by your filters`}
                              </Text>
                            </View>
                          </View>
                        ) : hasCacheReason ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Ionicons name="information-circle-outline" size={13} color="rgba(255,255,255,0.35)" />
                            <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: '600' }}>
                              {_REASON_LABELS[query._cacheReason] || 'No results'}
                            </Text>
                          </View>
                        ) : !isSGP && query.allBooksCount != null ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                            <Text style={[styles.resultsText, typeGradient]}>
                              {describeResultCounts({ lines: query.allBooksCount, cards: query.allBooksCards })}
                            </Text>
                            {query.hasMyBooks && <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>·</Text>}
                            {query.hasMyBooks && <Text style={[{ fontSize: 12, fontWeight: '600' }, typeGradient]}>
                              {`${query.myBooksCount} on My Books`}
                            </Text>}
                            {!!query.unreliableOnlyBooks && <Text style={{ fontSize: 11, color: '#f59e0b' }}>• prediction markets only</Text>}
                            {query.cached && <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>• Cached</Text>}
                            {!!query.durationMs && <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>• {(query.durationMs / 1000).toFixed(1)}s</Text>}
                          </View>
                        ) : (
                          <Text style={[styles.resultsText, isSGP && { color: '#a78bfa' }, isLive && { color: '#6ee7b7' }, isStraight && { color: '#93c5fd' }]}>
                            {query.sgpCount > 0
                              ? `${query.sgpCount} SGPs`
                              : isSGP ? '0 SGPs'
                              : focusCount != null ? `${focusCount} lines`
                              : `${query.results?.length || 0} lines`}
                            {query.cached && ' • Cached'}
                            {query.durationMs && ` • ${(query.durationMs / 1000).toFixed(1)}s`}
                          </Text>
                        )}
                      </View>
                      {/* Hide view button when focus book has no results; show it for below-threshold
                          so user can view with min books filter disabled */}
                      {!hasZeroForFocusBook && (
                        <GradientViewButton
                          isSGP={isSGP}
                          isLive={isLive}
                          onPress={() => handleViewResults(query)}
                        />
                      )}
                    </View>
                  );
                })()}

                {/* Error state */}
                {query.status === QueryStatus.ERROR && (
                  <View style={styles.errorRow}>
                    <Ionicons name="warning" size={14} color="#f87171" />
                    <Text style={styles.errorText} numberOfLines={1}>
                      {query.error || 'Query failed'}
                    </Text>
                    <PressableScale
                      style={styles.rerunButton}
                      onPress={() => retryQuery(query.id)}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="refresh" size={12} color="#fca5a5" />
                      <Text style={styles.rerunButtonText}>Rerun</Text>
                    </PressableScale>
                  </View>
                )}

                {/* Cancelled state */}
                {query.status === QueryStatus.CANCELLED && (
                  <Text style={styles.cancelledText}>Query cancelled</Text>
                )}
              </View>
              );
            })}

            {/* Clear all button */}
            {activeQueries.some(q => 
              q.status === QueryStatus.COMPLETED || 
              q.status === QueryStatus.ERROR || 
              q.status === QueryStatus.CANCELLED
            ) && (
              <PressableScale 
                style={styles.clearAllButton}
                onPress={clearCompletedQueries}
              >
                <Text style={styles.clearAllText}>Clear Completed</Text>
              </PressableScale>
            )}
          </ScrollView>
        )}
      </Animated.View>

      {/* Cancel Confirmation Modal - Use conditional render for better web support */}
      {confirmCancelId !== null && (
        <Modal
          visible={true}
          transparent
          animationType="fade"
          onRequestClose={dismissCancelModal}
        >
          <Pressable 
            style={styles.modalOverlay} 
            onPress={dismissCancelModal}
          >
            <Pressable 
              onPress={(e) => e.stopPropagation()}
            >
              <View style={styles.confirmModal}>
                <Text style={styles.confirmTitle}>Cancel Query?</Text>
                <Text style={styles.confirmMessage}>
                  Are you sure you want to cancel this query? Progress will be lost.
                </Text>
                <View style={styles.confirmButtons}>
                  <PressableScale 
                    style={[styles.confirmButton, styles.confirmButtonCancel]}
                    onPress={dismissCancelModal}
                  >
                    <Text style={styles.confirmButtonText}>Keep Running</Text>
                  </PressableScale>
                  <PressableScale 
                    style={[styles.confirmButton, styles.confirmButtonConfirm]}
                    onPress={confirmCancel}
                  >
                    <Text style={[styles.confirmButtonText, { color: '#f87171' }]}>Cancel Query</Text>
                  </PressableScale>
                </View>
              </View>
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </>
  );
};


export default BackgroundQueryModal;

